import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UsersService } from '../../services/users.service';
import { User } from '../../models';
import { UserDialogComponent } from './user-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog.component';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatPaginatorModule,
    MatSortModule,
    MatSnackBarModule,
    MatTooltipModule
  ],
  template: `
    <div class="users-container">
      <div class="page-header">
        <h1>Gestión de Usuarios</h1>
        <button mat-raised-button color="primary" (click)="openDialog()">
          <mat-icon>person_add</mat-icon>
          Nuevo Usuario
        </button>
      </div>

      <mat-card class="data-table-container">
        <div class="table-header">
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Buscar usuarios</mat-label>
            <input matInput [(ngModel)]="searchTerm" placeholder="Nombre o email...">
            <mat-icon matSuffix>search</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Estado</mat-label>
            <mat-select [(ngModel)]="statusFilter">
              <mat-option value="">Todos</mat-option>
              <mat-option value="active">Activos</mat-option>
              <mat-option value="inactive">Inactivos</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <table mat-table [dataSource]="filteredUsers" class="users-table">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Usuario</th>
            <td mat-cell *matCellDef="let user">
              <div class="user-cell">
                <div class="user-avatar">{{ getInitials(user.name) }}</div>
                <div class="user-info">
                  <span class="user-name">{{ user.name }}</span>
                  <span class="user-email">{{ user.email }}</span>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="role">
            <th mat-header-cell *matHeaderCellDef>Rol</th>
            <td mat-cell *matCellDef="let user">{{ roleLabel(user.role) }}</td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Estado</th>
            <td mat-cell *matCellDef="let user">
              <span class="status-badge" [ngClass]="user.status">
                {{ user.status === 'active' ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="lastLogin">
            <th mat-header-cell *matHeaderCellDef>Último Acceso</th>
            <td mat-cell *matCellDef="let user">
              {{ user.lastLogin ? (user.lastLogin | date:'dd/MM/yyyy HH:mm') : 'Nunca' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="createdAt">
            <th mat-header-cell *matHeaderCellDef>Creado</th>
            <td mat-cell *matCellDef="let user">{{ user.createdAt | date:'dd/MM/yyyy' }}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Acciones</th>
            <td mat-cell *matCellDef="let user">
              <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="Opciones">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="openDialog(user)">
                  <mat-icon>edit</mat-icon>
                  <span>Editar</span>
                </button>
                <button mat-menu-item (click)="toggleStatus(user)">
                  <mat-icon>{{ user.status === 'active' ? 'block' : 'check_circle' }}</mat-icon>
                  <span>{{ user.status === 'active' ? 'Desactivar' : 'Activar' }}</span>
                </button>
                <button mat-menu-item (click)="deleteUser(user)" class="delete-option">
                  <mat-icon>delete</mat-icon>
                  <span>Eliminar</span>
                </button>
              </mat-menu>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>

        <div class="table-footer">
          <span class="results-count">{{ filteredUsers.length }} usuarios encontrados</span>
        </div>
      </mat-card>
    </div>
  `,
  styles: [`
    .users-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h1 {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin: 0;
      }
    }

    .data-table-container {
      overflow: hidden;
      background: var(--surface-color);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
    }

    .table-header {
      display: flex;
      gap: 16px;
      padding: 16px 24px;
      border-bottom: 1px solid var(--border-color);
    }

    .search-field {
      flex: 1;
      max-width: 400px;
    }

    .filter-field {
      width: 150px;
    }

    mat-form-field {
      ::ng-deep .mdc-text-field--outlined {
        border-radius: 10px;
      }
    }

    .users-table {
      width: 100%;
    }

    .user-cell {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--primary-color);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 500;
    }

    .user-info {
      display: flex;
      flex-direction: column;
    }

    .user-name {
      font-weight: 500;
      color: var(--text-primary);
    }

    .user-email {
      font-size: 12px;
      color: var(--text-secondary);
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;

      &.active {
        background: rgba(76, 175, 80, 0.1);
        color: #2e7d32;
      }

      &.inactive {
        background: rgba(158, 158, 158, 0.1);
        color: #616161;
      }
    }

    .delete-option {
      color: #c62828;
    }

    .table-footer {
      padding: 16px 24px;
      border-top: 1px solid var(--border-color);
    }

    .results-count {
      font-size: 13px;
      color: var(--text-secondary);
    }

    ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }
  `]
})
export class UsersComponent implements OnInit {
  users: User[] = [];
  filteredUsers: User[] = [];
  searchTerm = '';
  statusFilter = '';
  displayedColumns = ['name', 'role', 'status', 'lastLogin', 'createdAt', 'actions'];

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private usersService = inject(UsersService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.usersService.getAllUsers().subscribe(users => {
      setTimeout(() => {
        this.users = users;
        this.applyFilters();
        this.cdr.detectChanges();
      }, 0);
    });
  }

  applyFilters() {
    this.filteredUsers = this.users.filter(user => {
      const matchesSearch = !this.searchTerm ||
        user.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesStatus = !this.statusFilter || user.status === this.statusFilter;

      return matchesSearch && matchesStatus;
    });
  }

getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }

  roleLabel(role: string): string {
    if (role === 'Admin') return 'Administrador';
    if (role === 'Empresa') return 'Empresa';
    return role || '-';
  }

  openDialog(user?: User) {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      width: '500px',
      data: { user, roles: ['Admin', 'Empresa'] }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (user) {
          this.usersService.updateUser(user.id, {
            username: result.username || result.name,
            email: result.email,
            role: result.role,
            status: result.status || user.status,
            password: result.password
          }).subscribe(updatedUser => {
            const index = this.users.findIndex(u => u.id === user.id);
            if (index !== -1) {
              this.users[index] = updatedUser;
              this.applyFilters();
            }
            this.snackBar.open('Usuario actualizado correctamente', 'Cerrar', { duration: 3000 });
          });
        } else {
          this.usersService.createUser({
            username: result.username || result.name,
            email: result.email,
            password: result.password,
            role: result.role,
            status: result.status || 'active'
          }).subscribe(newUser => {
            this.users.push(newUser);
            this.applyFilters();
            this.snackBar.open('Usuario creado correctamente', 'Cerrar', { duration: 3000 });
          });
        }
      }
    });
  }

  toggleStatus(user: User) {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    this.usersService.updateUser(user.id, {
      username: user.name,
      email: user.email,
      role: user.role,
      status: newStatus
    }).subscribe(updatedUser => {
      const index = this.users.findIndex(u => u.id === user.id);
      if (index !== -1) {
        this.users[index] = updatedUser;
        this.applyFilters();
      }
      this.snackBar.open(
        `Usuario ${newStatus === 'active' ? 'activado' : 'desactivado'} correctamente`,
        'Cerrar',
        { duration: 3000 }
      );
    });
  }

  deleteUser(user: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Usuario',
        message: `¿Estás seguro de que deseas eliminar al usuario "${user.name}"?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.usersService.deleteUser(user.id).subscribe(() => {
          this.users = this.users.filter(u => u.id !== user.id);
          this.applyFilters();
          this.snackBar.open('Usuario eliminado correctamente', 'Cerrar', { duration: 3000 });
        });
      }
    });
  }
}
